import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import userService from "../services/userService";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaCopy,
  FaUserPlus,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
  FaTrash,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [status, setStatus] = useState("active");
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [apiKey, setApiKey] = useState("");

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await userService.getAllUsers();
        setUsers(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (error) {
        toast.error(
          "ব্যবহারকারী লোড করতে সমস্যা: " +
            (error.response?.data?.error || error.message),
          { position: "top-right" }
        );
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Register new user
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await userService.register(
        username,
        password,
        role,
        status
      );
      toast.success("ব্যবহারকারী সফলভাবে নিবন্ধিত হয়েছে!", {
        position: "top-right",
      });
      setApiKey(response.data.apiKey);
      setUsername("");
      setPassword("");
      setRole("user");
      setStatus("active");
      const usersResponse = await userService.getAllUsers();
      setUsers(
        Array.isArray(usersResponse.data.data) ? usersResponse.data.data : []
      );
    } catch (error) {
      toast.error(
        "নিবন্ধন ত্রুটি: " + (error.response?.data?.error || error.message),
        { position: "top-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    if (window.confirm("আপনি কি এই ব্যবহারকারীকে মুছে ফেলতে চান?")) {
      setLoading(true);
      try {
        await userService.deleteUser(userId);
        toast.success("ব্যবহারকারী সফলভাবে মুছে ফেলা হয়েছে!", {
          position: "top-right",
        });
        setUsers(users.filter((user) => user._id !== userId));
      } catch (error) {
        toast.error(
          "মুছে ফেলার ত্রুটি: " +
            (error.response?.data?.error || error.message),
          { position: "top-right" }
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle user status
  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    setLoading(true);
    try {
      await userService.updateUser(userId, { status: newStatus });
      toast.success(`ব্যবহারকারীর স্ট্যাটাস ${newStatus} এ আপডেট হয়েছে!`, {
        position: "top-right",
      });
      setUsers(
        users.map((user) =>
          user._id === userId ? { ...user, status: newStatus } : user
        )
      );
    } catch (error) {
      toast.error(
        "স্ট্যাটাস আপডেট ত্রুটি: " +
          (error.response?.data?.error || error.message),
        { position: "top-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Update user
  const handleUpdate = async (e, userId) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userService.updateUser(userId, {
        username,
        password,
        role,
        status,
      });
      toast.success("ব্যবহারকারী সফলভাবে আপডেট হয়েছে!", {
        position: "top-right",
      });
      setEditingUser(null);
      setUsername("");
      setPassword("");
      setRole("user");
      setStatus("active");
      const response = await userService.getAllUsers();
      setUsers(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error(
        "আপডেট ত্রুটি: " + (error.response?.data?.error || error.message),
        { position: "top-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Set user data for editing
  const startEditing = (user) => {
    setEditingUser(user._id);
    setUsername(user.username);
    setPassword("");
    setRole(user.role);
    setStatus(user.status);
    setApiKey("");
  };

  // Copy API key to clipboard
  const copyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("API কী কপি করা হয়েছে!", { position: "top-right" });
  };

  return (
    <div className="admin-dashboard">
      <Header />
      <ToastContainer />
      <div className="container py-5">
        <div className="row g-4">
          <div className="col-lg-12">
            <div className="card shadow-lg p-4 gradient-bg">
              <h2 className="text-center mb-4 text-white">
                {editingUser ? "ব্যবহারকারী আপডেট" : "নতুন ব্যবহারকারী তৈরি"}
              </h2>
              <form
                onSubmit={
                  editingUser
                    ? (e) => handleUpdate(e, editingUser)
                    : handleRegister
                }
              >
                <div className="mb-3">
                  <label htmlFor="username" className="form-label text-white">
                    ইউজারনেম
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-pill"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label text-white">
                    পাসওয়ার্ড
                  </label>
                  <input
                    type="password"
                    className="form-control rounded-pill"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      editingUser ? "অপরিবর্তিত রাখতে ফাঁকা রাখুন" : ""
                    }
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="role" className="form-label text-white">
                    রোল
                  </label>
                  <select
                    className="form-select rounded-pill"
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={loading}
                  >
                    <option value="user">ইউজার</option>
                    <option value="admin">অ্যাডমিন</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="status" className="form-label text-white">
                    স্ট্যাটাস
                  </label>
                  <select
                    className="form-select rounded-pill"
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={loading}
                  >
                    <option value="active">অ্যাকটিভ</option>
                    <option value="inactive">ইনঅ্যাকটিভ</option>
                  </select>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-register rounded-pill px-4"
                    disabled={loading}
                  >
                    {editingUser ? <FaEdit /> : <FaUserPlus />}{" "}
                    {editingUser ? "আপডেট" : "নিবন্ধন"}
                  </button>
                  {editingUser && (
                    <button
                      type="button"
                      className="btn btn-cancel rounded-pill px-4"
                      onClick={() => {
                        setEditingUser(null);
                        setUsername("");
                        setPassword("");
                        setRole("user");
                        setStatus("active");
                        setApiKey("");
                      }}
                      disabled={loading}
                    >
                      বাতিল
                    </button>
                  )}
                </div>
              </form>
              {message && (
                <div
                  className={`alert mt-3 ${
                    message.includes("ত্রুটি")
                      ? "alert-danger"
                      : "alert-success"
                  }`}
                >
                  {message}
                </div>
              )}
              {apiKey && !editingUser && (
                <div className="alert alert-success mt-3">
                  <strong>API কী:</strong> {apiKey}
                  <br />
                  <button
                    className="btn btn-copy mt-2 rounded-pill"
                    onClick={() => copyApiKey(apiKey)}
                  >
                    <FaCopy /> API কী কপি করুন
                  </button>
                  <br />
                  <small className="text-muted">
                    এই কী নিরাপদে ব্যবহারকারীর সাথে শেয়ার করুন।
                  </small>
                </div>
              )}
            </div>
          </div>
          <div className="col-lg-12 mt-4">
            <div className="card shadow-lg p-4">
              <h2 className="text-center mb-4 gradient-text">
                ব্যবহারকারী তালিকা
              </h2>
              {loading && <div className="alert alert-info">লোড হচ্ছে...</div>}
              {users.length === 0 && !loading && (
                <div className="alert alert-warning">
                  কোনো ব্যবহারকারী পাওয়া যায়নি।
                </div>
              )}
              {users.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead className="gradient-bg text-white">
                      <tr>
                        <th>ইউজারনেম</th>
                        <th>WhatsApp নম্বর</th>
                        <th>রোল</th>
                        <th>স্ট্যাটাস</th>
                        <th>API কী</th>
                        <th>অ্যাকশন</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user._id} className="table-row-hover">
                          <td>{user.username}</td>
                          <td>{user.whatsappNumber || "N/A"}</td>
                          <td>
                            {user.role === "admin" ? "অ্যাডমিন" : "ইউজার"}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                user.status === "active"
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {user.status === "active"
                                ? "অ্যাকটিভ"
                                : "ইনঅ্যাকটিভ"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                className="text-truncate"
                                style={{ maxWidth: "150px" }}
                              >
                                {user.apiKey || "N/A"}
                              </span>
                              {user.apiKey && (
                                <button
                                  className="btn btn-copy ms-2"
                                  onClick={() => copyApiKey(user.apiKey)}
                                  title="API কী কপি করুন"
                                >
                                  <FaCopy />
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-edit me-2"
                              onClick={() => startEditing(user)}
                              disabled={loading}
                            >
                              <FaEdit /> এডিট
                            </button>
                            {user.username !== "admin" && (
                              <button
                                className="btn btn-toggle me-2"
                                onClick={() =>
                                  handleToggleStatus(user._id, user.status)
                                }
                                disabled={loading}
                              >
                                {user.status === "active" ? (
                                  <FaToggleOff />
                                ) : (
                                  <FaToggleOn />
                                )}
                                {user.status === "active"
                                  ? " নিষ্ক্রিয়"
                                  : " সক্রিয়"}
                              </button>
                            )}
                            {user.username !== "admin" && (
                              <button
                                className="btn btn-delete"
                                onClick={() => handleDelete(user._id)}
                                disabled={loading}
                              >
                                <FaTrash /> মুছুন
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
